import { Button, forwardRef } from '@chakra-ui/react';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ExternalLink,
  Link,
  LogIn,
  LogOut,
  Save,
  Trash2,
} from 'lucide-react';
import React, { ReactElement, ReactNode } from 'react';

import './CustomButton.scss';

interface CustomButtonProps {
  className?: string;
  children: ReactNode;
  onClick: (event: any) => void;
  variant?: 'primary' | 'secondary' | 'ghost';
  type?: 'solid' | 'outline'; // only applicable for 'primary' button variant
  size?: 'xs' | 'sm' | 'md' | 'lg';
  isLoading?: boolean;
  isDisabled?: boolean;
  isMobile?: boolean;
  leftIcon?: '' | 'chevronLeft';
  rightIcon?:
    | ''
    | 'chevronRight'
    | 'chevronUp'
    | 'chevronDown'
    | 'externalLink'
    | 'logIn'
    | 'logOut'
    | 'link'
    | 'save'
    | 'delete';
  spinner?: ReactElement;
}

const CustomButton = forwardRef<CustomButtonProps, 'button'>(
  (
    {
      className = '',
      children,
      onClick,
      variant = 'primary',
      type = 'solid', // only applicable for 'primary' button variant
      size = 'md',
      isLoading = false,
      isDisabled = false,
      isMobile = false,
      leftIcon = '',
      rightIcon = '',
      spinner,
    },
    ref
  ) => {
    const variantClassName = (variant: string) => {
      if (variant === 'primary') {
        return `${variant} ${type}`;
      } else return variant;
    };

    const isMobileClassName = isMobile ? 'mobile' : '';

    const leftIconToDisplay = (leftIcon: string) => {
      if (leftIcon === 'chevronLeft') {
        return <ChevronLeft className={'button-icon chevron-left'} />;
      } else {
        return <></>;
      }
    };

    const rightIconToDisplay = (rightIcon: string) => {
      switch (rightIcon) {
        case 'chevronRight':
          return <ChevronRight className={'button-icon chevron-right'} />;
        case 'chevronUp':
          return <ChevronUp className={'button-icon chevron-up'} />;
        case 'chevronDown':
          return <ChevronDown className={'button-icon chevron-down'} />;
        case 'externalLink':
          return <ExternalLink className={'button-icon external-link'} />;
        case 'logIn':
          return <LogIn className={'button-icon log-in'} />;
        case 'logOut':
          return <LogOut className={'button-icon log-out'} />;
        case 'link':
          return <Link className={'button-icon link'} />;
        case 'save':
          return <Save className={'button-icon save'} />;
        case 'delete':
          return <Trash2 className={'button-icon delete'} />;
        default:
          return <></>;
      }
    };

    return (
      <Button
        className={`custom-button ${className} ${variantClassName(
          variant
        )} ${isMobileClassName} ${size}`}
        size={size}
        isLoading={isLoading}
        isDisabled={isDisabled}
        onClick={onClick}
        spinner={spinner}
        ref={ref}
        iconSpacing={0}
      >
        {leftIconToDisplay(leftIcon)}
        {children}
        {rightIconToDisplay(rightIcon)}
      </Button>
    );
  }
);

export default CustomButton;
